// chartHelpers.js

export function generateLabels(chartStartDate, intervalMinutes = 15, startTime = "00:00") {
  // Parse startTime (HH:mm format) to get hours and minutes
  const [startHour, startMinute] = (startTime || "00:00").split(":").map(Number);
  const dayKey = chartStartDate 
    ? `${new Date(new Date(chartStartDate).setHours(0,0,0,0)).toISOString().slice(0,10)}_${startTime}` 
    : `AUTO_LASTYEAR_${startTime}`;
  const INTERVAL = intervalMinutes * 60 * 1000;
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const startBase = chartStartDate ? new Date(chartStartDate) : new Date(Date.now() - 365*ONE_DAY);
  // Set the start time to the specified hour and minute
  const start = new Date(startBase);
  start.setHours(startHour, startMinute, 0, 0);
  const endTime = start.getTime() + 365*ONE_DAY;
  const count = Math.ceil((endTime - start.getTime())/INTERVAL);
  const tmp = new Array(count);
  let t = start.getTime();
  for (let i=0;i<count;i++, t+=INTERVAL) tmp[i] = new Date(t);
  return { labels: tmp, key: dayKey };
}

// Generates labels for a single 24-hour day anchored to baseDate (defaults to Jan 1 of previous year).
// Used when recurrence is DAILY — interval drives how many points fit in 24h.
export function generateDailyLabels(intervalMinutes = 15, baseDate) {
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const base = baseDate ? new Date(baseDate) : new Date(new Date().getFullYear() - 1, 0, 1);
  const start = new Date(base);
  start.setHours(0, 0, 0, 0);
  const key = `DAILY_${intervalMinutes}_${start.toISOString().slice(0, 10)}`;
  const INTERVAL = intervalMinutes * 60 * 1000;
  const count = Math.ceil(ONE_DAY / INTERVAL);
  // count + 1: last entry is the end-of-day sentinel (00:00 next day)
  // so d3.extent(labels) spans the full 24 hours
  const tmp = new Array(count + 1);
  let t = start.getTime();
  for (let i = 0; i <= count; i++, t += INTERVAL) tmp[i] = new Date(t);
  return { labels: tmp, key };
}

export function formatValue(value) {
  if (value == null || isNaN(value)) return '0';
  const num = Number(value);
  const abs = Math.abs(num);
  if (abs >= 1) {
    // Always show as integer with thousands separator
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(num);
  }
  if (abs >= 1e-3) {
    // Between 0.001 and 1 -> up to 4 decimals (trim trailing zeros) without regex backtracking
    let str = num.toFixed(4);
    // Trim trailing zeros deterministically
    while (str.endsWith('0')) str = str.slice(0, -1);
    // Remove trailing dot if present
    if (str.endsWith('.')) str = str.slice(0, -1);
    return str === '' ? '0' : str;
  }
  // Very small -> 6 decimals, avoid scientific
  let tiny = num.toFixed(6);
  while (tiny.endsWith('0')) tiny = tiny.slice(0, -1);
  if (tiny.endsWith('.')) tiny = tiny.slice(0, -1);
  return tiny === '' || tiny === '-0' ? '0' : tiny;
}

export function calculateStats(data) {
  if (!data.length) return { max: '0', min: '0', mean: '0', energy: '0' };
  let max = -Infinity, min = Infinity, sum = 0;
  for (let i=0;i<data.length;i++) {
    const v = data[i];
    if (typeof v === 'number' && !isNaN(v)) {
      if (v > max) max = v;
      if (v < min) min = v;
      sum += v;
    }
  }
  if (max === -Infinity) { max = 0; min = 0; sum = 0; }
  const mean = sum / data.length;
  const energy = mean * data.length;
  return { max: formatValue(max), min: formatValue(min), mean: formatValue(mean), energy: formatValue(energy) };
}

export function progressiveSample(arr, labels, innerWidth) {
  if (labels.length <= innerWidth * 1.2) return arr;
  const stride = Math.max(1, Math.floor(labels.length / innerWidth));
  const idx = [];
  for (let i=0;i<labels.length;i+=stride) idx.push(i);
  if (idx[idx.length-1] !== labels.length-1) idx.push(labels.length-1);
  const out = new Array(idx.length);
  for (let i=0;i<idx.length;i++) out[i] = arr[idx[i]];
  return out;
}
